// @ts-nocheck
import collect from "collect.js";
import PaymentManager from "../../api/PaymentManager";
import { PaymentMethod, PAYMENT_METHODS } from "../../exports/Constants";
import {
  CreatePaymentOptionDataInterface,
  PaymentOptionPropertiesInterface,
} from "../../exports/Interfaces";
import * as stripeJs from "@stripe/stripe-js";
import ReduxActions from "../../../store/ReduxActions";
import { CompanyDetailsInterface } from "../../exports/Interfaces";

class PaymentHelperResource {
  public checkPaymentMethodIsSupported(
    item: PaymentOptionPropertiesInterface,
    supportedPaymentMethods: string[]
  ): boolean {
    if (supportedPaymentMethods === undefined) {
      return true;
    }

    return !!collect(supportedPaymentMethods).first(
      (paymentMethod: string) => paymentMethod === item?.payment_method_id
    );
  }

  private getPaymentOptions(): PaymentOptionPropertiesInterface[] {
    return ReduxActions.getStore()?.paymentOptions?.payment_options;
  }

  private getCompany(): CompanyDetailsInterface {
    return ReduxActions.getStore()?.initialData?.company;
  }

  private getPaymentMethods(): PaymentOptionPropertiesInterface[] {
    return PAYMENT_METHODS();
  }

  public initPaymentOptions(blacklist = []): PaymentOptionPropertiesInterface[] {
    try {
      return collect(this.getPaymentMethods())
        .reject(
          (item: PaymentOptionPropertiesInterface) =>
            !this.checkPaymentMethodIsSupported(
              item,
              this.getCompany()?.supported_payment_methods
            ) || blacklist.includes(item.payment_method_id)
        )
        .map((item: PaymentOptionPropertiesInterface) => {
          let newItem = item;

          const savedPaymentItem = this.getPaymentOptions()?.find(
            (paymentOption: PaymentOptionPropertiesInterface) =>
              paymentOption.payment_method_id === item.payment_method_id
          );

          if (savedPaymentItem) {
            return {
              ...newItem,
              ...savedPaymentItem,
              paymentMethodSaved: true,
            };
          }
          return { ...newItem, paymentMethodSaved: false, is_default: false };
        })
        .toArray();
    } catch (error) {
      return this.getPaymentMethods();
    }
  }

  public fillBlacklistWithDisabledWallets = (enabledWallets, blacklist = []) =>
    collect(blacklist).when(
      ! enabledWallets.includes(PaymentMethod.ApplePay),
      (collection) => collection.push(PaymentMethod.ApplePay)
    ).when(
      ! enabledWallets.includes(PaymentMethod.GooglePay),
      (collection) => collection.push(PaymentMethod.GooglePay)
    ).toArray()

  public getDefaultPaymentMethod(blacklist = []): PaymentOptionPropertiesInterface {
    try {
      return collect(this.initPaymentOptions(blacklist))?.first(
        (item: PaymentOptionPropertiesInterface) => item?.is_default
      );
    } catch (error) {
      return null;
    }
  }

  public async savePaymentMethod(
    paymentOptions: PaymentOptionPropertiesInterface[],
    selectedPaymentMethod: PaymentOptionPropertiesInterface,
    setupIntent?: stripeJs.SetupIntent
  ) {
    if (
      this.paymentMethodExists(paymentOptions, selectedPaymentMethod)
    ) {
      //update payment method
      return PaymentManager.putDefaultPaymentOption({
        payment_option_id: collect(paymentOptions).first(
          (paymentMethod: PaymentOptionPropertiesInterface) =>
            paymentMethod?.payment_method_id ===
            selectedPaymentMethod?.payment_method_id
        )?.id,
      });
    }

    //create payment method
    return PaymentManager.postPaymentOption(
      this.computeCreatePaymentData(selectedPaymentMethod, setupIntent)
    );
  }

  private computeCreatePaymentData(
    selectedPaymentMethod: PaymentOptionPropertiesInterface,
    setupIntent?: stripeJs.SetupIntent
  ): CreatePaymentOptionDataInterface {
    let data: CreatePaymentOptionDataInterface = {
      company_id: this.getCompany()?.id,
      payment_method_id: selectedPaymentMethod?.payment_method_id,
    };

    switch (selectedPaymentMethod?.payment_method_id) {
      case PaymentMethod.Card:
      case PaymentMethod.SepaDebit:
      case PaymentMethod.Sofort:
        data = {
          ...data,
          external_payment_method_id: setupIntent?.payment_method,
        };
    }

    return data;
  }

  public paymentMethodExists(
    paymentOptions: PaymentOptionPropertiesInterface[],
    selectedPaymentMethod: PaymentOptionPropertiesInterface
  ): boolean {
    try {
      return collect(paymentOptions)?.contains(
        (paymentMethod: PaymentOptionPropertiesInterface) =>
          paymentMethod?.payment_method_id ===
          selectedPaymentMethod?.payment_method_id
      );
    } catch (error) {
      return false;
    }
  }
}

export default new PaymentHelperResource();
